/**
 * AgentGuiRenderer — Dynamic JSON-to-React renderer for GUI elements
 */
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'

interface GuiElementDefinition {
  element_type: string
  fields?: { name: string; type: string; label: string; required?: boolean; options?: { value: string; label: string }[]; validation?: Record<string, any> }[]
  labels?: Record<string, string>
  validation_rules?: Record<string, any>
  default_values?: Record<string, any>
  action_handlers?: { action: string; label: string; variant?: string; confirmation_required?: boolean }[]
  layout_hints?: Record<string, any>
  data?: any
  entity_references?: { entity_type: string; entity_id: string; display_text: string }[]
}

interface AgentGuiRendererProps {
  definition: GuiElementDefinition
  onAction?: (action: string, data: any) => void
}

const ENTITY_ROUTES: Record<string, string> = {
  service_case: '/service-cases', case: '/service-cases', job: '/jobs',
  customer: '/customers', invoice: '/invoices', estimate: '/estimates',
  appointment: '/appointments', asset: '/assets',
}

export function AgentGuiRenderer({ definition, onAction }: AgentGuiRendererProps) {
  const navigate = useNavigate()

  if (!definition || !definition.element_type) {
    return <div className="text-xs text-destructive">Invalid GUI element definition</div>
  }

  switch (definition.element_type) {
    case 'form': return <FormElement def={definition} onAction={onAction} />
    case 'data_table': return <DataTableElement def={definition} />
    case 'summary_card': return <SummaryCardElement def={definition} navigate={navigate} />
    case 'confirmation_dialog': return <ConfirmationElement def={definition} onAction={onAction} />
    case 'action_button_group': return <ActionButtonGroup def={definition} onAction={onAction} />
    case 'status_timeline': return <StatusTimeline def={definition} />
    default: return <div className="text-xs text-destructive">Unknown element type: {definition.element_type}</div>
  }
}

function FormElement({ def, onAction }: { def: GuiElementDefinition; onAction?: (a: string, d: any) => void }) {
  const [values, setValues] = useState<Record<string, any>>(def.default_values || {})

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onAction?.('form_submit', values)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      {def.fields?.map(field => (
        <div key={field.name}>
          <label className="text-xs font-medium">{field.label}{field.required && ' *'}</label>
          {field.type === 'select' ? (
            <select value={values[field.name] || ''} onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
              className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm" aria-label={field.label}>
              <option value="">Select...</option>
              {field.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : field.type === 'checkbox' ? (
            <input type="checkbox" checked={!!values[field.name]} onChange={e => setValues(v => ({ ...v, [field.name]: e.target.checked }))}
              className="mt-1" aria-label={field.label} />
          ) : field.type === 'textarea' ? (
            <textarea value={values[field.name] || ''} onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
              className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm" rows={3} aria-label={field.label} />
          ) : (
            <input type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
              value={values[field.name] || ''} onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
              className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm" aria-label={field.label} />
          )}
        </div>
      ))}
      <button type="submit" className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground">Submit</button>
    </form>
  )
}

function DataTableElement({ def }: { def: GuiElementDefinition }) {
  const rows = Array.isArray(def.data) ? def.data : []
  const columns = def.labels ? Object.entries(def.labels) : (rows[0] ? Object.keys(rows[0]).map(k => [k, k]) : [])

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr>{columns.map(([key, label]) => <th key={key} className="border-b px-2 py-1 text-left font-medium">{label}</th>)}</tr></thead>
        <tbody>{rows.map((row: any, i: number) => (
          <tr key={i}>{columns.map(([key]) => <td key={key} className="border-b px-2 py-1">{String(row[key] ?? '')}</td>)}</tr>
        ))}</tbody>
      </table>
    </div>
  )
}

function SummaryCardElement({ def, navigate }: { def: GuiElementDefinition; navigate: (path: string) => void }) {
  const entries = def.labels ? Object.entries(def.labels) : []
  return (
    <div className="space-y-1 rounded border border-border p-2">
      {entries.map(([key, label]) => (
        <div key={key} className="flex justify-between text-xs">
          <span className="text-muted-foreground">{label}</span>
          <span>{def.data?.[key] ?? '—'}</span>
        </div>
      ))}
      {def.entity_references?.map(ref => (
        <button key={ref.entity_id} onClick={() => navigate({ to: `${ENTITY_ROUTES[ref.entity_type] || ''}/${ref.entity_id}` })}
          className="text-xs text-primary underline">{ref.display_text}</button>
      ))}
    </div>
  )
}

function ConfirmationElement({ def, onAction }: { def: GuiElementDefinition; onAction?: (a: string, d: any) => void }) {
  return (
    <div className="space-y-2 rounded border border-border p-3">
      {def.data?.message && <p className="text-sm">{def.data.message}</p>}
      <div className="flex gap-2">
        {def.action_handlers?.map(handler => (
          <button key={handler.action} onClick={() => onAction?.(handler.action, {})}
            className={`rounded px-3 py-1 text-xs ${
              handler.variant === 'destructive' ? 'bg-destructive text-destructive-foreground'
                : handler.variant === 'secondary' ? 'border border-input bg-background'
                  : 'bg-primary text-primary-foreground'
            }`}>{handler.label}</button>
        ))}
      </div>
    </div>
  )
}

function ActionButtonGroup({ def, onAction }: { def: GuiElementDefinition; onAction?: (a: string, d: any) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {def.action_handlers?.map(handler => (
        <button key={handler.action} onClick={() => onAction?.(handler.action, {})}
          className={`rounded px-3 py-1.5 text-xs ${
            handler.variant === 'destructive' ? 'bg-destructive text-destructive-foreground'
              : handler.variant === 'secondary' ? 'border border-input bg-background'
                : 'bg-primary text-primary-foreground'
          }`}>{handler.label}</button>
      ))}
    </div>
  )
}

function StatusTimeline({ def }: { def: GuiElementDefinition }) {
  const events = Array.isArray(def.data) ? def.data : []
  return (
    <div className="space-y-2">
      {events.map((event: any, i: number) => (
        <div key={i} className="flex gap-2 text-xs">
          <div className="flex flex-col items-center">
            <div className="h-2 w-2 rounded-full bg-primary" />
            {i < events.length - 1 && <div className="w-px flex-1 bg-border" />}
          </div>
          <div>
            <div className="font-medium">{event.title || event.status}</div>
            {event.timestamp && <div className="text-muted-foreground">{event.timestamp}</div>}
            {event.description && <div className="text-muted-foreground">{event.description}</div>}
          </div>
        </div>
      ))}
    </div>
  )
}
